import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  SearchIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  ChevronRightIcon,
} from 'lucide-angular';
import { Category } from '../../../types/category.types';
import { smartTitle } from '../../../utils/pipes/smart-capitalize';
import { CategoriesService } from '../../../services/categories.service';
import { CartService } from '../../../services/cart.service';

@Component({
  selector: 'allas-search-results',

  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="space-y-4">
      <!-- Estado de loading -->
      <div *ngIf="loading" class="space-y-3">
        <div *ngFor="let i of [1, 2, 3, 4, 5]" class="animate-pulse">
          <div class="bg-white rounded-xl p-4 border border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div class="h-3 bg-gray-100 rounded w-1/2"></div>
              </div>
              <div class="w-20 h-8 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sin resultados -->
      <div
        *ngIf="!loading && results.length === 0 && searchTerm"
        class="text-center py-12"
      >
        <div class="w-16 h-16 mx-auto mb-4 text-gray-300">
          <lucide-icon [name]="searchIcon" class="w-full h-full"></lucide-icon>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          No encontramos "{{ searchTerm }}"
        </h3>
        <p class="text-gray-500 mb-4">
          Intenta con otros términos o revisa nuestras categorías principales
        </p>
      </div>

      <!-- Estado inicial (sin búsqueda) -->
      <div *ngIf="!loading && !searchTerm" class="text-center py-12">
        <div class="w-16 h-16 mx-auto mb-4 text-gray-300">
          <lucide-icon [name]="searchIcon" class="w-full h-full"></lucide-icon>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          Busca repuestos para tu vehículo
        </h3>
        <p class="text-gray-500">
          Escribe el nombre del repuesto que necesitas
        </p>
      </div>

      <!-- Resultados -->
      <div *ngIf="!loading && results.length > 0" class="space-y-3">
        <!-- Header de resultados -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-900">
            Repuestos encontrados
          </h2>
          <span
            class="text-sm bg-blue-100 text-blue-600 px-3 py-1 rounded-full"
          >
            {{ results.length }} resultado{{ results.length !== 1 ? 's' : '' }}
          </span>
        </div>

        <!-- Lista de resultados -->
        <div
          *ngFor="let category of results; trackBy: trackByCategory"
          class="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-sm transition-all duration-200"
        >
          <div class="p-4">
            <div class="flex flex-col gap-3">
              <!-- Información de la categoría -->
              <div
                class="flex-1 cursor-pointer"
                (click)="onCategoryClick(category)"
              >
                <h3
                  class="text-base font-medium text-gray-900 hover:text-blue-600 transition-colors mb-1"
                >
                  {{ smartTitle(category.name) }}
                </h3>
                <p
                  *ngIf="getParentName(category)"
                  class="text-sm text-gray-500"
                >
                  En {{ getParentName(category) }}
                </p>
              </div>

              <!-- Controles según tipo de categoría -->
              <div
                class="flex items-center justify-between pt-2 border-t border-gray-100"
              >
                <!-- Para categorías hoja: Controles del carrito -->
                <div
                  *ngIf="isLeafCategory(category)"
                  class="flex items-center gap-3"
                >
                  <!-- Controles de cantidad -->
                  <div class="flex items-center bg-gray-50 rounded-lg">
                    <button
                      (click)="decrementVisualQuantity(category.id)"
                      class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-600 rounded-l-lg border-r border-gray-200 transition-colors duration-200 flex items-center justify-center"
                      [class.opacity-50]="getVisualQuantity(category.id) <= 1"
                    >
                      <lucide-icon
                        [name]="minusIcon"
                        class="w-3 h-3"
                      ></lucide-icon>
                    </button>

                    <div class="w-12 text-center py-1">
                      <span class="text-sm font-medium text-gray-900">{{
                        getVisualQuantity(category.id)
                      }}</span>
                    </div>

                    <button
                      (click)="incrementVisualQuantity(category.id)"
                      class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-600 rounded-r-lg border-l border-gray-200 transition-colors duration-200 flex items-center justify-center"
                    >
                      <lucide-icon
                        [name]="plusIcon"
                        class="w-3 h-3"
                      ></lucide-icon>
                    </button>
                  </div>

                  <!-- Botón carrito -->
                  <button
                    (click)="addToCartWithQuantity(category)"
                    class="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm"
                    [class]="
                      isInCart(category.id)
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-appPrimary-500 text-white hover:bg-blue-700'
                    "
                  >
                    <lucide-icon
                      [name]="cartIcon"
                      class="w-4 h-4"
                    ></lucide-icon>
                    <span>{{
                      isInCart(category.id) ? 'En carrito' : 'Agregar'
                    }}</span>
                    <span *ngIf="isInCart(category.id)" class="text-xs"
                      >({{ getQuantity(category.id) }})</span
                    >
                  </button>
                </div>

                <!-- Para categorías padre: Botón "Ver repuestos" -->
                <div
                  *ngIf="!isLeafCategory(category)"
                  class="flex items-center"
                >
                  <button
                    (click)="onCategoryClick(category)"
                    class="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    <span>Ver repuestos</span>
                    <lucide-icon
                      [name]="chevronRightIcon"
                      class="w-3 h-3"
                    ></lucide-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class SearchResultsComponent {
  @Input() results: Category[] = [];
  @Input() searchTerm: string = '';
  @Input() loading: boolean = false;

  @Output() categoryClick = new EventEmitter<Category>();
  @Output() addToCart = new EventEmitter<{
    category: Category;
    quantity: number;
  }>();

  // Servicios
  private categoriesService = inject(CategoriesService);
  private cartService = inject(CartService);

  // Estado local para cantidades visuales
  visualQuantities = signal<Record<string, number>>({});

  // Iconos
  readonly searchIcon = SearchIcon;
  readonly plusIcon = PlusIcon;
  readonly minusIcon = MinusIcon;
  readonly cartIcon = ShoppingCartIcon;
  readonly chevronRightIcon = ChevronRightIcon;

  smartTitle = smartTitle;

  onCategoryClick(category: Category): void {
    this.categoryClick.emit(category);
  }

  // Métodos del carrito
  isInCart(categoryId: number): boolean {
    return this.cartService.isItemInCart(categoryId);
  }

  getQuantity(categoryId: number): number {
    return this.cartService.getQuantityInCart(categoryId);
  }

  // Métodos para cantidades visuales
  getVisualQuantity(categoryId: number): number {
    const cartQuantity = this.getQuantity(categoryId);
    const visualQuantity = this.visualQuantities()[categoryId];

    if (cartQuantity > 0) {
      return cartQuantity;
    }

    return visualQuantity || 1;
  }

  incrementVisualQuantity(categoryId: number): void {
    const currentVisual = this.visualQuantities()[categoryId] || 1;
    this.visualQuantities.update((quantities) => ({
      ...quantities,
      [categoryId]: currentVisual + 1,
    }));
  }

  decrementVisualQuantity(categoryId: number): void {
    const currentVisual = this.visualQuantities()[categoryId] || 1;
    if (currentVisual > 1) {
      this.visualQuantities.update((quantities) => ({
        ...quantities,
        [categoryId]: currentVisual - 1,
      }));
    }
  }

  isLeafCategory(category: Category): boolean {
    return this.categoriesService.isLeafCategory(category);
  }

  addToCartWithQuantity(category: Category): void {
    const visualQuantity = this.getVisualQuantity(category.id);
    this.addToCart.emit({ category, quantity: visualQuantity });
  }

  getParentName(category: Category): string | null {
    const parent = this.categoriesService.getParentCategory(category.slug);
    return parent ? smartTitle(parent.name) : null;
  }

  trackByCategory(index: number, category: Category): string {
    return category.slug;
  }
}
